import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Status } from '@/components/ui/Status'
import { splitVerdict } from '@/lib/errors'
import { captureStatusMeta, type CaptureOutcome } from '@/lib/status'
import { formatNumber, formatRelative } from '@/lib/format'
import { Pagination } from '@/components/judgments/Pagination'
import type { Capture, PageMeta } from '@/types/api'

/**
 * A capture's outcome as a badge.
 *
 * Thin wrapper over the shared registry so the drawer and the list cannot
 * disagree; the three-state logic itself lives in `lib/status`.
 */
export function CaptureOutcomeBadge({ capture }: { capture: CaptureOutcome }) {
  return <Status meta={captureStatusMeta(capture)} variant="badge" />
}

/** One figure from a capture, labelled. */
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-2xs tracking-[0.04em] text-ink-muted uppercase">{label}</dt>
      <dd className="tabular truncate text-sm font-medium text-ink">{value}</dd>
    </div>
  )
}

const ArchiveIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
    <path
      d="M3 7.5h18M4.5 7.5v11a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-11M3 7.5L4.8 4.4A1.5 1.5 0 0 1 6.1 3.6h11.8a1.5 1.5 0 0 1 1.3.8L21 7.5M9.5 11.5h5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Why a capture is short, in one line.
 *
 * The engine's verdict repeats the badge ("INCOMPLETE") and then repeats
 * itself ("the capture failed") before saying anything new. Both are dropped;
 * the remedy follows as a quieter second line, because it is the part someone
 * scanning the list can actually act on.
 */
function CaptureVerdict({ text }: { text: string }) {
  const { problem, remedy } = splitVerdict(text)
  return (
    <div className="mt-2 border-l-2 border-line-strong pl-2.5">
      <p className="text-sm text-ink-secondary">{problem}</p>
      {remedy ? <p className="mt-0.5 text-xs text-ink-muted">{remedy}</p> : null}
    </div>
  )
}

export interface CaptureListProps {
  captures: Capture[]
  onFilterByCapture?: (captureId: number) => void
  /**
   * Paging state, when the caller is fetching a page at a time. Omit it and the
   * list renders whatever it was handed with no pager — which is what the
   * summary views want.
   */
  meta?: PageMeta
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
}

/**
 * Captures with their completeness verdict.
 *
 * This is the audit trail: which searches produced the stored data, and which
 * of them the portal truncated. The verdict sits beside the label rather than
 * inside a tooltip, because "is this data set complete" is the question this
 * list exists to answer.
 */
export function CaptureList({
  captures,
  onFilterByCapture,
  meta,
  onPageChange,
  onPageSizeChange,
}: CaptureListProps) {
  // Counted separately because they are not the same problem: a failed capture
  // stored nothing, while a capped or partial one holds real records that are
  // merely incomplete. Reporting them as one number told you how many rows
  // were imperfect but not whether any data was actually missing.
  const failed = captures.filter(
    (capture) => !capture.isCapped && !capture.isComplete && capture.recordsFound === 0,
  ).length
  const short = captures.filter(
    (capture) =>
      capture.isCapped || (!capture.isComplete && capture.recordsFound > 0),
  ).length

  return (
    <Card>
      <CardHeader
        title="Captures"
        description={
          meta && meta.totalItems > captures.length
            ? `Each ingested search, and whether the portal showed all its matches. ${formatNumber(meta.totalItems)} in total.`
            : 'Each ingested search, and whether the portal showed all its matches.'
        }
        meta={
          captures.length > 0 ? (
            <Status
              meta={
                failed > 0
                  ? {
                      label:
                        short > 0
                          ? `${failed} failed · ${short} short`
                          : `${failed} failed`,
                      tone: 'critical',
                      hint: 'A failed capture stored nothing at all.',
                    }
                  : short > 0
                    ? {
                        label: `${short} incomplete`,
                        tone: 'warning',
                        hint: 'These stored real records, but not every match.',
                      }
                    : { label: 'All complete', tone: 'good' }
              }
            />
          ) : null
        }
      />

      {captures.length === 0 ? (
        <EmptyState
          size="compact"
          icon={<ArchiveIcon />}
          title="No captures yet"
          description="Captures appear here once an ingestion job succeeds."
        />
      ) : (
        <ul>
          {captures.map((capture) => (
            <li
              key={capture.id}
              className="border-b border-line-subtle px-4 py-3 transition-colors last:border-b-0 hover:bg-surface-hover/60"
            >
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="truncate text-base font-medium text-ink">
                    {capture.label}
                  </span>
                  <CaptureOutcomeBadge capture={capture} />
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="text-xs text-ink-muted">
                    {formatRelative(capture.createdAt)}
                  </span>
                  {onFilterByCapture ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onFilterByCapture(capture.id)}
                    >
                      View records
                    </Button>
                  ) : null}
                </div>
              </div>

              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                <Metric label="Stored" value={formatNumber(capture.recordsFound)} />
                <Metric
                  label="Site reported"
                  value={
                    capture.reportedTotal > 0 ? formatNumber(capture.reportedTotal) : '-'
                  }
                />
                <Metric
                  label="Pages"
                  value={capture.pagesRead > 0 ? formatNumber(capture.pagesRead) : '-'}
                />
                <Metric
                  label="Details read"
                  value={formatNumber(capture.detailsFetched)}
                />
              </dl>

              {capture.isCapped || !capture.isComplete ? (
                <CaptureVerdict text={capture.verdict} />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/*
        Rendered only when the caller is paging, and only when there is more
        than one page — a pager under a single page of results is a control that
        does nothing, which invites the reader to wonder what they are missing.
      */}
      {meta && onPageChange && onPageSizeChange && meta.totalPages > 1 ? (
        <div className="border-t border-line-subtle px-4 py-3">
          <Pagination
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      ) : null}
    </Card>
  )
}
